// ═══════════════════════════════════════════════════════════════════
//  WHAT A BOOKING CARRIES, MADE READABLE
//
//  Two sources, and the split between them is deliberate.
//
//  `describeFormData` is what the applicant filled in, read off the CMS
//  booking request: the questionnaire, the ZIP, the date of birth, the
//  utility account number and the Social Security number. CMS is the
//  system of record for all of it. It is a fixed, known set of fields,
//  so it is destructured and labelled by hand.
//
//  `describeMetadata` is Cal's own booking metadata — flat string pairs
//  written by whoever made the booking. It used to carry the household
//  answers too, and no longer does: Cal serializes a booking's metadata
//  into the attendee's own confirmation email, so those answers moved to
//  columns CMS controls. What is left is the confirmation code, who
//  booked it, and anything a future caller decides to add.
//
//  That last part is why the metadata half stays open-ended: nothing
//  there drops a pair it does not recognise, and an unknown key is shown
//  with a label derived from the key itself. Hiding it would mean a
//  booking carrying information nobody on staff can see, which is the
//  failure this view exists to prevent.
// ═══════════════════════════════════════════════════════════════════

import type { BookingFormData } from "../bookingCalendarApi"

/** One metadata pair, ready to draw. */
export interface MetadataEntry {
  /** The raw key, which is also the React list key. */
  key: string
  /** What to call it on screen. */
  label: string
  /**
   * The value, formatted where the key says how, or null when the booking carries nothing for it.
   *
   * A null is drawn as a placeholder rather than dropped: staff reading a booking over the phone
   * need to see that a question came back empty, not be left to wonder whether it was asked.
   */
  value: string | null
  /** Fixed-width, for values read out or quoted rather than read: the confirmation code. */
  mono?: boolean
}

/**
 * The metadata keys CMS itself still writes, in the order a staff member reads them.
 *
 * `BookingConfirmationCode.MetadataKey` and `PublicBookingOperations.BuildStaffMetadata` are where
 * these come from. The household keys that used to be here have moved to the booking request — see
 * `describeFormData`. Anything not listed still appears; see `describeMetadata`.
 */
const KNOWN_KEYS: readonly { key: string; label: string; mono?: boolean }[] = [
  { key: "confirmationCode", label: "Confirmation number", mono: true },
  { key: "referenceCode", label: "Reference code", mono: true },
  { key: "bookedBy", label: "Booked by" },
  { key: "duplicateOverride", label: "Duplicate override" },
]

const CURRENCY = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
})

/**
 * A label for a key nothing here was written for: "referralSource" → "Referral source".
 *
 * Metadata keys are camelCase by convention, so splitting on the case boundary recovers the words.
 * A key that is not camelCase comes back as it went in, which is still better than not showing it.
 */
export function humanizeKey(key: string): string {
  const words = key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim()

  return words ? words.charAt(0).toUpperCase() + words.slice(1).toLowerCase() : key
}

/**
 * A date the way staff read one: "Mar 4, 1978" from an ISO "1978-03-04".
 *
 * Split and rebuilt rather than passed to `new Date()`, which parses a bare yyyy-MM-dd as UTC
 * midnight and then renders it in the viewer's zone — west of Greenwich that is the previous day,
 * which on a date of birth is a wrong answer that looks like a right one.
 *
 * Anything that is not an ISO date comes back as it arrived. Showing what is actually stored beats
 * showing "Invalid Date".
 */
function formatIsoDate(value: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (!match) return value

  const [, year, month, day] = match
  const rendered = new Date(Number(year), Number(month) - 1, Number(day))

  return Number.isNaN(rendered.getTime())
    ? value
    : rendered.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })
}

/** "123456789" → "123-45-6789". Anything that is not nine digits is shown exactly as stored. */
function formatSsn(value: string): string {
  const digits = value.replace(/\D/g, "")
  return digits.length === 9 ? `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}` : value
}

/**
 * The booking's metadata as rows to draw: the keys CMS writes first, in their reading order, then
 * everything else alphabetically so the list is stable between bookings.
 *
 * A key Cal carries with nothing in it is drawn as an empty row rather than dropped, so that what
 * the dialog shows is what the booking holds. Keys that are absent altogether are not invented —
 * this half has no schema to invent them from, unlike `describeFormData`.
 */
export function describeMetadata(metadata: Record<string, string> | undefined): MetadataEntry[] {
  if (!metadata) return []

  const present = Object.entries(metadata)
    .filter(([, value]) => typeof value === "string")

  const known = KNOWN_KEYS
    .filter(({ key }) => present.some(([k]) => k === key))
    .map(({ key, label, mono }) => ({
      key,
      label,
      value: blankToNull(metadata[key]!),
      mono,
    }))

  const rest = present
    .filter(([key]) => !KNOWN_KEYS.some(k => k.key === key))
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, value]) => ({ key, label: humanizeKey(key), value: blankToNull(value) }))

  return [...known, ...rest]
}

/**
 * What the applicant filled in, as rows to draw, in the order somebody on the phone asks for them:
 * who they are, then how to reach them, then the household picture.
 *
 * A closed set of fields, unlike the metadata above, because this side has a schema — so every
 * field is drawn, whether or not this booking carries one. The two channels ask for different
 * amounts, and a staff booking taken at a counter legitimately has most of the questionnaire
 * missing; an empty row says which questions those were, which is what somebody on the phone
 * needs before asking for the answer.
 *
 * The Social Security number is formatted but never masked here. The server already decided whether
 * this viewer may see it — it is simply absent from the payload otherwise — and a component that
 * masked what the server chose to send would be a second, quieter access rule disagreeing with the
 * first. An SSN that was never given and one the retention sweep has erased both come back empty,
 * so the row states neither: it is blank, like any other unanswered question.
 */
export function describeFormData(form: BookingFormData | undefined): MetadataEntry[] {
  if (!form) return []

  return [
    { key: "dateOfBirth", label: "Date of birth", value: form.dateOfBirth ? formatIsoDate(form.dateOfBirth) : null },
    { key: "ssn", label: "SSN", value: form.ssn ? formatSsn(form.ssn) : null, mono: true },
    text("energyAccountNumber", "Energy account", form.energyAccountNumber, { mono: true }),
    text("address", "Address", form.address),
    text("zipCode", "ZIP code", form.zipCode),
    { key: "householdSize", label: "Household size", value: form.householdSize != null ? String(form.householdSize) : null },

    // The form asks for monthly income, not annual, and a figure this size is easy to read as the
    // wrong one. The label says which.
    {
      key: "householdIncome",
      label: "Monthly income",
      value: form.householdIncome != null && Number.isFinite(form.householdIncome)
        ? CURRENCY.format(form.householdIncome)
        : null,
    },

    text("energySource", "Energy source", form.energySource),
    text("pseService", "PSE service", form.pseService),

    // Stated either way, unlike everything above it. "No" here is a real answer that governs whether
    // this household gets texted about the appointment, so it is never blank.
    { key: "smsOptIn", label: "Appointment texts", value: form.smsOptIn ? "Agreed" : "Not agreed" },

    // Empty on a booking the applicant made themselves, which is exactly what an empty row says
    // here — nobody on staff booked it.
    text("createdByUpn", "Booked by", form.createdByUpn),
  ]
}

/** One row for an optional string, empty when there is nothing behind it. */
function text(
  key: string,
  label: string,
  value: string | undefined,
  options?: { mono?: boolean }
): MetadataEntry {
  return { key, label, value: blankToNull(value), mono: options?.mono }
}

/** A trimmed value, or null for one that is absent or nothing but whitespace. */
function blankToNull(value: string | undefined): string | null {
  const trimmed = value?.trim()
  return trimmed ? trimmed : null
}

/**
 * Program names as staff say them, keyed by the server's AmsBookingPrograms names. The same labels
 * the review queue uses. A name not listed here — a program added later — is shown as it arrives
 * rather than dropped.
 */
const PROGRAM_LABELS: Readonly<Record<string, string>> = {
  pse: "PSE",
  liheap: "LIHEAP",
}

/** "Pse" → "PSE"; an unknown program as the server named it. */
export function programLabel(name: string): string {
  return PROGRAM_LABELS[name.toLowerCase()] ?? name
}

/**
 * What the household screening found (AB#766): the programs still open to the household and the
 * ones it appears already to have been served for this program year.
 *
 * Three answers, kept apart on purpose. A booking the screening never checked — made while the
 * eligibility check was off, or adopted from Cal — says "Not checked", because its program columns
 * are a default rather than a finding and "eligible for both" would be a claim nobody made. A
 * checked booking with nothing in a list says "None". Anything else lists the programs.
 *
 * Empty when there is no request behind the appointment, like `describeFormData`.
 */
export function describeScreening(form: BookingFormData | undefined): MetadataEntry[] {
  if (!form) return []

  return [
    { key: "eligiblePrograms", label: "Eligible programs", value: programList(form.eligiblePrograms) },
    { key: "blockedPrograms", label: "Already served", value: programList(form.blockedPrograms) },
  ]
}

function programList(programs: readonly string[] | null | undefined): string {
  if (programs == null) return "Not checked"
  return programs.length === 0 ? "None" : programs.map(programLabel).join(", ")
}

/**
 * Where the matched CMS person lives, or null when there is no link to draw: no person was matched,
 * or the viewer may not open people (`people:view`). The id alone travels with the booking; what the
 * person page shows is that page's own permission to decide.
 */
export function matchedPersonHref(form: BookingFormData | undefined, canViewPeople: boolean): string | null {
  const id = form?.matchedPersonId?.trim()
  return id && canViewPeople ? `/people/${encodeURIComponent(id)}` : null
}
